<template>
  <div class="mt-3 billing-container">
    <billing-transactions-filters
      v-model="filter"
      :hasFilters="hasFilters"
      :loading="loadingMoreCharges"
      :showing="showing"
      ref="filterHeight"
    />

    <div class="mt-4 mb-1 w-100 d-flex flex-row justify-content-end">
      <div class="d-flex flex-row justify-content-end align-items-start">
        <div class="d-flex flex-row justify-content-end align-items-center">
          <top-paginator
            :total="listTotal"
            :page="filter.page"
            :perPage="filter.pageSize"
            @change-page="changePage"
          />
        </div>
      </div>
    </div>
    <div
      class="clients-list"
      :class="hasFilters ? 'clients-list-filtered' : ''"
      :style="`--filter-height:` + filterHeight()"
    >
      <grid
        ref="grid"
        :columns="columns"
        :data="items"
        :store="gridConfig.store"
        :selectionMode="gridConfig.selectionMode"
        :columnLines="gridConfig.columnLines"
        :groupFeature="false"
        :autoHeight="true"
        :searchFeature="true"
        :stripeFeature="false"
        @storesort="listeners.storeSort"
        @selectionchange="listeners.selectionChange"
        @renderrows="hookDropdownButtons"
      />
    </div>

    <payment-view-refund-modal
      :type="paymentViewModal.type"
      :formTitle="
        paymentViewModal.type === 'view' ? 'Payment' : 'Refund payment'
      "
      :paymentId="paymentViewModal.id"
      v-if="paymentViewModal.show"
      v-model="paymentViewModal.show"
      @onHidden="
        () => {
          paymentViewModal.show = false;
          paymentViewModal.id = null;
          paymentViewModal.type = 'view';
          fetchInitialData();
          fetchTransactions();
        }
      "
      @changeType="paymentViewModal.type = 'refund'"
      @success="
        () => {
          paymentViewModal.id = null;
          paymentViewModal.type = 'view';
          fetchInitialData();
        }
      "
    />
  </div>
</template>

<script>
import {
  formatEndDateSql,
  formatStartDateSql,
  formatDateOnlyPretty
} from "../../../helpers/date";
import { mapActions, mapState } from "vuex";
import Grid from "../../../third_party/grid/Grid.vue";
import TopPaginator from "../../controls/TopPaginator.vue";
import paymentServer from "../../../server/payment";
import BillingTransactionsFilters from "./BillingTransactionsFilters.vue";
import PaymentViewRefundModal from "./PaymentViewRefundModal.vue";

export default {
  name: "BillingTransactionsIndex",
  props: ["showing"],
  components: {
    Grid,
    TopPaginator,
    BillingTransactionsFilters,
    PaymentViewRefundModal
  },
  data() {
    const self = this;
    return {
      filter: {
        patientId: [],
        paymentType: [],
        paymentMethodType: [],
        dateTo: null,
        dateFrom: null,
        amount: {
          from: null,
          to: null
        },
        searchPhrase: "",
        page: 0,
        pageSize: 50,
        orderBy: "createdAt",
        orderByDescending: true
      },
      paymentModal: {
        show: false
      },
      paymentViewModal: {
        show: false,
        paymentId: null,
        type: "view"
      },
      loadingMoreCharges: false,
      list: [],
      listTotal: 0,
      refetchTransactions: true,
      gridConfig: {
        minHeight: "20em",
        selectionMode: {
          row: false,
          checkbox: false,
          rowCheckboxSelection: false,
          showCheckAll: false
        },
        columnLines: false,
        store: {
          // Default sorters
          sorters: [{ field: "dateAndTime", ascending: false }]
        }
      },
        listeners: { 
            selectionChange: selectionEvent => {
                self.transactionsSelected = selectionEvent.selection.map(x => x.data);
            },
        storeSort: sortData => {
          if (sortData.sorters.length === 1) {
            const sorter = sortData.sorters[0];
            const ascending = sorter.ascending;
            self.filter.orderBy = sorter.field;
            self.filter.orderByDescending = !ascending;
            self.changePage(0);
          }
        }
      },
      transactionsSelected: []
    };
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess", "showError", "show", "hide"]),
    async fetchInitialData() {
      this.fetchTransactions(true);
    },    
    // eslint-disable-next-line no-unused-vars
    fetchTransactions: function(instantReload = false) {
      if (this.lastTimeout) {
        clearTimeout(this.lastTimeout);
        this.lastTimeout = null;
      }
      this.lastTimeout = setTimeout(
        async () => {
          let result = await paymentServer.query({
            patientId: this.filter.patientId.map(s => s.value),
            paymentType: this.filter.paymentType.map(s => s.value),
            searchPhrase: this.filter.searchPhrase,
            dateFrom: formatStartDateSql(this.filter.dateFrom),
            dateTo: formatEndDateSql(this.filter.dateTo),
            amountFrom: this.filter.amount.from || null,
            amountTo: this.filter.amount.to || null,
            paymentMethodType: this.filter.paymentMethodType.map(s => s.value),
            page: this.filter.page,
            pageSize: this.filter.pageSize,
            orderBy: this.filter.orderBy,
            orderByDescending: this.filter.orderByDescending
          });
          this.list = result.transactions;
          this.listTotal = result.count;
        },
        instantReload ? 0 : 1000
      );
    },
    filterHeight() {
      return (
        (this.$refs.filterHeight
          ? this.$refs.filterHeight.$el.clientHeight
          : 0) + "px"
      );
    },
    changePage(page) {
      this.filter.page = page;
      this.fetchTransactions(true);
      this.scrollGridToTop();
    },
    scrollGridToTop() {
      const el = document.getElementsByClassName("b-vertical-overflow");
      Array.from(el).forEach(e => {
        if (e.scrollTop > 0) e.scrollTop = 0;
      });
    }
  },
  computed: {
    ...mapState("layout", {
      isPracticeSuiteActive: state => state.isPracticeSuiteActive,
      isCollaborateMDActive: state => state.isCollaborateMDActive,
      currentUser: state => state.currentUser,
      systemSetting: state => state.systemSetting,
      assignedToMe: state => state.assignedToMeBillingIndex
    }),
    hasFilters() {
      return (
        this.filter.patientId.length > 0 ||
        this.filter.paymentType.length > 0 ||
        this.filter.paymentMethodType.length > 0 ||
        this.filter.dateFrom ||
        this.filter.dateTo ||
        this.filter.amount.from ||
        this.filter.amount.to
      );
    },
    items() {
      return this.list.map(item => {
        return {
          ...item
        };
      });
    },
    columns() {
      const self = this;
      return [
        {
          text: "Patient",
          field: "patient",
          type: "template",
          flex: 1,
          editor: false,
          template: ({ record }) => {
            return `${record.data.patient.mrn} - ${record.data.patient.firstName} ${record.data.patient.lastName}`;
          },
          sortable() {
            return self.filter.orderByDescending ? -1 : 1;
          }
        },
        {
          text: "Payment/Adjustment",
          field: "type",
          type: "template",
          flex: 1,
          editor: false,
          template: ({ record }) => {
            return record.data.type === "Refund"
              ? `Adjustment`
              : `Payment ${
                  record.data.refunded
                    ? `<span class="badge-v2 badge-neutral">Refunded</span>`
                    : record.data.partiallyRefunded
                    ? `<span class="badge-v2 badge-neutral">Partially Refunded</span>`
                    : ``
                }`;
          },
          sortable() {
            return self.filter.orderByDescending ? -1 : 1;
          }
        },
        {
          text: "Payment Type",
          field: "method",
          type: "template",
          width: 160,
          editor: false,
          template: data => {
            let method;
            switch (data.record.data.method) {
              case 0:
                method = `Credit/Debit Card ***${data.record.data.cardInfo
                  ?.lastFour || ""}`;
                break;
              case 1:
                method = "Cash";
                break;
              case 2:
                method = `Check ${
                  data.record.data.type !== "Refund"
                    ? `- ${data.record.data.checkNumber}`
                    : ``
                }`;
                break; 
              case 3:
                method = `External ${
                  data.record.data.type !== "Refund"
                    ? `- ${data.record.data.externalMethod}`
                    : ``
                }`;
                break;
              default:
                break;
            }
            return method;
          },
          sortable() {
            return self.filter.orderByDescending ? -1 : 1;
          }
        },
        {
          text: "Amount",
          field: "totalAmount",
          type: "template",
          flex: 1,
          editor: false,
          template: ({ record }) => {
            return `<span style="color: ${
              record.data.totalAmount > 0 && record.data.type !== "Refund"
                ? `green`
                : `red`
            }">${
              record.data.totalAmount > 0 && record.data.type !== "Refund"
                ? `+`
                : `-`
            } ${this.toCurrencyValue(record.data.totalAmount)}</span>`;
          },
          sortable() {
            return self.filter.orderByDescending ? -1 : 1;
          }
        },
        {
          text: "Payment Date",
          field: "paymentDate",
          type: "template",
          flex: 1,
          editor: false,
          template: ({ record }) => {
            return `${formatDateOnlyPretty(record.paymentDate)}`;
          },
          sortable() {
            return self.filter.orderByDescending ? -1 : 1;
          }
        },
        {
          text: "",
          type: "action",
          align: "right",
          cellCls: "pointer-cell",
          width: 80,
          actions: [
            {
              tooltip: "View",
              renderer: () =>
                `<i class="fa fa-search-plus color-custom-dark-grey hover-text-primary b-action-item  grid-action-icon"></i>`,
              onClick: ({ record }) => {
                this.paymentViewModal.id = record.data.id;
                this.paymentViewModal.show = true;
              }
            },
            {
              tooltip: "More",
              renderer: ({ record }) => {
                const actions = [];

                actions.push({
                  content: "Refund",
                  callback: () => {
                    if (record.data.type !== "Refund") {
                      this.paymentViewModal.id = record.data.id;
                      this.paymentViewModal.type = "refund";
                      this.paymentViewModal.show = true;
                    }
                  }
                });

                actions.push({
                  content: "Delete",
                  callback: () => {
                    this.$dialog
                      .alert(
                        {
                          title: "Are you sure you want to delete this record?"
                        },
                        {
                          view: "confirm-delete"
                        }
                      )
                      .then(async () => {
                        paymentServer.delete(record.data.id);
                        this.fetchTransactions();
                      });
                  }
                });

                return this.createDropdownButton({
                  items:
                    record.data.type !== "Refund" && !record.data.refunded
                      ? actions
                      : actions.filter(action => action.content !== "Refund"),
                  menuWidth: 150
                });
              }
            }
          ]
        }
      ];
    }
  },
  watch: {
    "filter.patientId": function() {
      this.changePage(0);
      this.fetchTransactions();
    },
    "filter.createdById": function() {
      this.changePage(0);
      this.fetchTransactions();
    },
    "filter.dateFrom": function() {
      this.changePage(0);
      this.fetchTransactions();
    },
    "filter.dateTo": function() {
      this.changePage(0);
      this.fetchTransactions();
    },
    "filter.searchPhrase": function() {
      this.fetchTransactions();
    },
    showing: {
      handler: function(newValue) {
        if (newValue) {
          this.fetchInitialData();
        }
        if (newValue && this.refetchTransactions) {
          this.fetchInitialData(true);
        }
      },
      immediate: true
    }
  }
};
</script>

<style scoped>
.filter-select {
  height: 50px;
  width: 300px;
  padding-top: 5px;
}

.clients-list {
  height: calc(100vh - 240px - var(--filter-height));
  overflow-y: auto;
}

.clients-list >>> .b-grid-container .b-container {
  max-height: calc(100vh - 240px - var(--filter-height)) !important;
}

.border-yellow {
  border-color: #ffc107 !important;
}

.separator-or {
  margin-bottom: -13px;
}

.dg-main-content {
  max-width: 550px;
}
</style>

<style>
.filter-select .vs__dropdown-toggle {
  height: 100%;
  border-radius: 0;
  border: 0;
}

.patient-picker-hide-options .vs__dropdown-menu {
  display: none;
}

.patient-picker-hide-options.vs--open .vs__dropdown-toggle {
  border-bottom-color: #4044e6 !important;
  border-bottom-width: 2px;
}
</style>
